import { takeLeading, takeEvery, call, put, all } from "redux-saga/effects";
import {
  eventsSetAll,
  eventAddOne,
  eventUpdateOne,
  eventRemoveOne,
  addChosenEvent,
  removeChosenEvent,
  addOneToHistory,
  removeOneFromHistory,
  changeHistory,
  removeOneToNotificationHistory,
  changeNotificationHistory,
  setScheduleErrors,
} from "../slices/events.slice";
import { modalEdit } from "../slices/modal.slice";
import { contentEdit } from "../slices/modal.slice";
import {
  loadingEdit,
  errorEdit,
  successEdit,
  loadingErrorsEdit,
} from "../slices/events.slice";
import { scheduleService, stickersService } from "../../services";
import { studioSetAll } from "../slices/studio.slice";
import { toast } from "react-toastify";
import {
  stickerAddOne,
  removeChosenSticker,
  stickerRemoveOne,
  addChosenSticker
} from "../slices/stickers.slice";
import { enrichStickerWithColors } from "./index";
import {
  handleStudentsOnEventUpdate,
  detachStudentFromEvent,
  attachStudentToEvent,
} from "./events.students.helpers";


// Helper: enrich all stickers in a board response with color data from Redux entities
function* enrichResponseStickers(data) {
  if (!data || !data.studios) return data;
  const enrichedStudios = [];
  for (const studio of data.studios) {
    const enrichedStickers = [];
    for (const sticker of (studio.scheduleSticker || [])) {
      const enriched = yield* enrichStickerWithColors(sticker);
      enrichedStickers.push(enriched);
    }
    enrichedStudios.push({ ...studio, scheduleSticker: enrichedStickers });
  }
  return { ...data, studios: enrichedStudios };
}

export function* getAll(action) {
  yield put(loadingEdit(true));

  try {
    const response = yield call(
      scheduleService.getOne,
      action.payload,
      action.query
    );
    if (response.data === null) {
      yield put(eventsSetAll({ 
        studios: [],
        boardDays: {} 
      }));
      yield put(studioSetAll([]));
    } else {
      const enrichedData = yield* enrichResponseStickers(response.data);
      yield put(eventsSetAll(enrichedData));
      yield put(studioSetAll(enrichedData.studios || []));
    }
  } catch (error) {
    if (error.response) {
      yield put(errorEdit(error.response.data.message));
      yield put(contentEdit(error.response.data.message));
      yield put(modalEdit("Confirm"));
    } else {
      yield put(errorEdit("Something has gone wrong. Please try again later"));
      yield put(
        contentEdit("Something has gone wrong. Please try again later")
      );
      yield put(modalEdit("Confirm"));
    }
  } finally {
    yield put(loadingEdit(false));
  }
}

export function* getWeekSchedule(action) {
  yield put(loadingEdit(true));

  try {
    const response = yield call(
      scheduleService.getWeek,
      action.query
    );
    const enrichedWeekData = yield* enrichResponseStickers(response.data);
    yield put(eventsSetAll(enrichedWeekData));
    yield put(studioSetAll(enrichedWeekData.studios || []));
  } catch (error) {
    if (error.response) {
      yield put(errorEdit(error.response.data.message));
      yield put(contentEdit(error.response.data.message));
      yield put(modalEdit("Confirm"));
    } else {
      yield put(errorEdit("Something has gone wrong. Please try again later"));
      yield put(
        contentEdit("Something has gone wrong. Please try again later")
      );
      yield put(modalEdit("Confirm"));
    }
  } finally {
    yield put(loadingEdit(false));
  }
}

function* updateOne(action) {
  yield put(loadingEdit(true));
  try {
    const response = yield call(scheduleService.update, action.payload.body);
    const { sameLevels, sameTeacher, teacherAvailableTime, teacherLevelAssignment, outOfWorkingHours, studentOverlap } =
      response.data || {};
    if (
      sameLevels !== "ok" ||
      sameTeacher !== "ok" ||
      outOfWorkingHours !== "ok" ||
      teacherAvailableTime !== "ok" ||
      teacherLevelAssignment !== "ok" ||
      studentOverlap !== "ok"
    ) {
      yield put(
        changeNotificationHistory({
          ...action.payload.body,
          notification: response.data.message,
        })
      );
      Object.values({
        sameLevels,
        sameTeacher,
        teacherAvailableTime,
        teacherLevelAssignment,
        outOfWorkingHours,
        studentOverlap
      }).forEach((item) => {
        if (item !== "ok") toast.warn(item);
      });
    } else {
      yield put(removeOneToNotificationHistory(action.payload.body.id));
    }

    const enrichedBody = yield* enrichStickerWithColors(action.payload.body);
    
    // Preserve students list from attachedStudents or prevEvent
    const students = action.payload.attachedStudents || action.payload.prevEvent?.students || [];
    const enrichedBodyWithStudents = {
      ...enrichedBody,
      students: students,
    };
    
    if (!action.payload.isUndo) {
      const event = {
        reversedType: action.type,
        eventBody: action.payload.prevEvent,
      };
      yield put(addOneToHistory(event));
      yield put(modalEdit(null));
    } else {
      yield put(removeOneFromHistory());
    }
    const eventPayload = {
      ...action.payload.body,
      students: students,
    };
    yield put(eventUpdateOne(enrichedBodyWithStudents));
    yield* handleStudentsOnEventUpdate({
      prevEvent: action.payload.prevEvent,
      eventPayload,
      eventId: action.payload.body.id,
    });
    yield put(addChosenEvent(enrichedBodyWithStudents));
    
    // Also update chosenSticker with students to preserve them if sticker is used elsewhere
    yield put(addChosenSticker(enrichedBodyWithStudents));

    yield put(removeChosenSticker());
    yield put(modalEdit(null));
  } catch (error) {
    if (error.response) {
      yield put(errorEdit(error.response.data.message));
      yield put(contentEdit(error.response.data.message));
      yield put(modalEdit("Confirm"));
    } else {
      yield put(errorEdit("Something has gone wrong. Please try again later"));
      yield put(
        contentEdit("Something has gone wrong. Please try again later")
      );
      yield put(modalEdit("Confirm"));
    }
  } finally {
    yield put(loadingEdit(false));
  }
}

function* detachStudentFromEventWithErrorsHandled(action) {
  try {
    yield* detachStudentFromEvent(action);
  } catch (error) {
    if (error.response) {
      yield put(errorEdit(error.response.data.message));
      yield put(contentEdit(error.response.data.message));
    } else {
      yield put(errorEdit("Something has gone wrong. Please try again later"));
      yield put(
        contentEdit("Something has gone wrong. Please try again later")
      );
    }
  } finally {
    // no global loading state change to keep modal untouched
  }
}

function* addOne(action) {
  yield put(loadingEdit(true));
  const { sticker, deleteSticker } = action.payload;
  try {
    const response = yield call(scheduleService.create, action.payload.body);
    // Merge sticker's levelsId/teachersId into the schedule response before enriching.
    // The backend create response sometimes omits these IDs, causing color enrichment
    // to return empty arrays and the card to render grey until a page refresh.
    const scheduleWithIds = {
      ...response.data.schedule,
      levelsId: response.data.schedule.levelsId?.length
        ? response.data.schedule.levelsId
        : (sticker?.levelsId || action.payload.body.levelsId || []),
      teachersId: response.data.schedule.teachersId?.length
        ? response.data.schedule.teachersId
        : (sticker?.teachersId || action.payload.body.teachersId || []),
    };
    const enrichedSchedule = yield* enrichStickerWithColors(scheduleWithIds);
    yield put(eventAddOne(enrichedSchedule));
    
    if (deleteSticker) {
      yield put(stickerRemoveOne(sticker.id));
    }

    yield put(successEdit("New event has created"));

    const { sameLevels, sameTeacher, teacherAvailableTime, teacherLevelAssignment, outOfWorkingHours, studentOverlap  } =
      response.data || {};
    if (
      sameLevels !== "ok" ||
      sameTeacher !== "ok" ||
      outOfWorkingHours !== "ok" ||
      teacherAvailableTime !== "ok" ||
      teacherLevelAssignment !== "ok" ||
      studentOverlap !== "ok"
    ) {
      yield put(
        changeNotificationHistory({
          ...action.payload.body,
          notification: response.data.message,
        })
      );
      Object.values({
        sameLevels,
        sameTeacher,
        teacherAvailableTime,
        teacherLevelAssignment,
        outOfWorkingHours,
        studentOverlap
      }).forEach((item) => {
        if (item !== "ok") toast.warn(item);
      });
    }
    // if it's not undo feature add to history
    if (!action.payload.isUndo) {
      const event = {
        reversedType: "EVENTS_REMOVE_ONE",
        eventBody: enrichedSchedule,
        addSticker: !sticker.isReusable,
        sticker: sticker,
      };
      yield put(addOneToHistory(event));
    } else {
      yield put(
        changeHistory({
          newId: enrichedSchedule.id,
          oldId: action.payload.body.id,
        })
      );
      yield put(removeOneFromHistory());
    }
  } catch (error) {
      if (error.response.data.message) {
        if (
        error.response.data.message ===
        "Not available to create more stickers in your subscription plan"
      ) {
        yield put(
          contentEdit(
            "Not available to create more stickers in your subscription plan"
          )
        );
        yield put(modalEdit("Confirm"));
      }
      yield put(errorEdit(error.response.data.message));
      yield put(contentEdit(error.response.data.message));
      yield put(modalEdit("Confirm"));
    } else {
      yield put(errorEdit("Something has gone wrong. Please try again later"));
      yield put(
        contentEdit("Something has gone wrong. Please try again later")
      );
      yield put(modalEdit("Confirm"));
    }
  } finally {
    yield put(loadingEdit(false));
  }
}

function* removeOne(action) {
  yield put(loadingEdit(true));
  const { body, addSticker, deleteSticker } = action.payload;
  try {
    // Don't delete schedule if we're creating a sticker from it (when dragging to events-pull)
    if (!addSticker) {
      yield call(scheduleService.delete, { userId: body.userId, scheduleId: body.id });
    }
    yield put(eventRemoveOne({ studioId: body.studioId, id: body.id }));
    yield put(removeChosenEvent(action.payload.id));
    yield put(removeOneToNotificationHistory(body.id))
    let sticker = {}
    if (addSticker) {
      const responseSticker = yield call(stickersService.create, action.payload.body);
      const enrichedSticker = yield* enrichStickerWithColors(responseSticker.data.sticker);
      // Preserve students from response if available
      const stickerWithStudents = {
        ...enrichedSticker,
        students: responseSticker.data.sticker.students || enrichedSticker.students || []
      };
      yield put(stickerAddOne(stickerWithStudents));
      sticker = stickerWithStudents;
    }

    if (!action.payload.isUndo) {
      const event = {
        reversedType: 'EVENTS_ADD_ONE',
        eventBody: body,
        deleteSticker: deleteSticker,
        sticker
      }
      yield put(addOneToHistory(event))
    } else {
      yield put(changeHistory({ newId: sticker.id, oldId: action.payload.sticker.id }))
      yield put(removeOneFromHistory())
    }
    yield put(modalEdit(null))
  } catch (error) {
    if (error.response) {
      yield put(errorEdit(error.response.data.message));
      yield put(contentEdit(error.response.data.message));
      yield put(modalEdit("Confirm"));
    } else {
      yield put(errorEdit("Something has gone wrong. Please try again later"));
      yield put(
        contentEdit("Something has gone wrong. Please try again later")
      );
      yield put(modalEdit("Confirm"));
    }
  } finally {
    yield put(loadingEdit(false));
  }
}

export function* getErrors(action) {
  yield put(loadingErrorsEdit(true));
  try {
    const response = yield call(scheduleService.getErrors, action.payload);
    yield put(setScheduleErrors(response.data));
  } catch (error) {
    if (error.response) {
      yield put(errorEdit(error.response.data.message));
      yield put(contentEdit(error.response.data.message));
      yield put(modalEdit("Confirm"));
    } else {
      yield put(errorEdit("Something has gone wrong. Please try again later"));
      yield put(
        contentEdit("Something has gone wrong. Please try again later")
      );
      yield put(modalEdit("Confirm"));
    }
  } finally {
    yield put(loadingErrorsEdit(false));
  }
}
function* attachStudentToEventWithErrorsHandled(action) {
  try {
    yield put(loadingEdit(true));
    yield* attachStudentToEvent(action);
  } catch (error) {
    if (error.response) {
      yield put(errorEdit(error.response.data.message));
      yield put(contentEdit(error.response.data.message));
      yield put(modalEdit("Confirm"));
    } else {
      yield put(errorEdit("Something has gone wrong. Please try again later"));
      yield put(
        contentEdit("Something has gone wrong. Please try again later")
      );
      yield put(modalEdit("Confirm"));
    }
  } finally {
    yield put(loadingEdit(false));
  }
}

export function* getAllErrors(action) {
  yield put(loadingErrorsEdit(true));
  try {
    const { userId, boardId, days } = action.payload;
    const requests = days.map(day =>
      call(scheduleService.getErrors, { userId, boardId, dayOfTheWeek: day })
    );
    const responses = yield all(requests);
    const combined = responses.flatMap(r => r.data || []);
    yield put(setScheduleErrors(combined));
  } catch (error) {
    if (error.response) {
      yield put(errorEdit(error.response.data.message));
      yield put(contentEdit(error.response.data.message));
      yield put(modalEdit("Confirm"));
    } else {
      yield put(errorEdit("Something has gone wrong. Please try again later"));
      yield put(contentEdit("Something has gone wrong. Please try again later"));
      yield put(modalEdit("Confirm"));
    }
  } finally {
    yield put(loadingErrorsEdit(false));
  }
}

export default function* watchEventsSaga() {
  yield takeLeading("EVENTS_SET_ALL", getAll);
  yield takeLeading("EVENTS_SET_WEEK", getWeekSchedule);
  yield takeLeading("EVENTS_ADD_ONE", addOne);
  yield takeLeading("EVENTS_UPDATE_ONE", updateOne);
  yield takeLeading("EVENT_STUDENT_DETACH", detachStudentFromEventWithErrorsHandled);
  yield takeLeading("EVENT_STUDENT_ATTACH", attachStudentToEventWithErrorsHandled);
  yield takeLeading("EVENTS_REMOVE_ONE", removeOne);
  yield takeLeading("GET_ERRORS", getErrors);
  yield takeEvery("GET_ALL_ERRORS", getAllErrors);
}
